import { RecommendationFeature } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { CoreDomainSearchStore } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useInfiniteQuery } from 'react-query';
import { createAdvancedQuery } from './useRecommendations.utils';
import { declaredExperimentsField, useDeclaredExperiments } from './declaredExperiments';
import { useDisplayPrices } from './useDisplayPrices';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { getSharedQueryClient } from '@wix/premium-fed-sdk/@apis-infra-react';
import { ExperimentSpecs } from '../constants';
const LIMIT = 10;
const DEFAULT_FEATURES = [RecommendationFeature.PRICING, RecommendationFeature.LABEL_MATCHING];

// Under the lazy-prices experiment, PRICING is dropped from the recommendations
// call and resolved afterwards via `useDisplayPrices`, keeping the initial call
// lean for a faster time-to-first-result.
export const isLazyPricesEnabled = () => {
  var _getPremiumEssentials;
  return Boolean((_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.ResultsLazyPrices]);
};
const resolveFeatures = features => isLazyPricesEnabled() ? features.filter(feature => feature !== RecommendationFeature.PRICING) : features;
const getHttpErrorStatus = error => {
  var _httpError$response;
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const httpError = error;
  return ((_httpError$response = httpError.response) == null ? void 0 : _httpError$response.status) ?? httpError.status;
};
const classifyRecommendationsError = error => {
  if (error instanceof Error && error.message === 'Network Error') {
    return 'NetworkIssue';
  }
  if (getHttpErrorStatus(error) === 429) {
    return 'RateLimited';
  }
  return 'TechnicalIssue';
};
const DEFAULT_QUERY = 'mystunningwebsite';
const getRecommendationsQueryKey = _ref => {
  let {
    query,
    features,
    tlds,
    hasValidVouchers,
    uniqueSldsOnly,
    productType,
    experimentIds = []
  } = _ref;
  return ['listRecommendations', query, features, tlds, hasValidVouchers, ...(uniqueSldsOnly ? [uniqueSldsOnly] : []), ...(productType ? [productType] : []),
  // The declared ids change what the server returns, so they have to key the cache too — this map
  // is hand-built, so unlike the generated hooks it does not vary on the request by itself.
  ...(experimentIds.length ? [experimentIds] : [])];
};
export const prefetchRecommendations = async function (_temp) {
  let {
    productType,
    hasValidVouchers,
    // Not a hook, so it cannot read the provider. Callers that declare the arm pass their ids in;
    // without them the prefetch would warm a key the real query never reads.
    experimentIds = []
  } = _temp === void 0 ? {} : _temp;
  const {
    tlds,
    ...submittedFilters
  } = CoreDomainSearchStore.getStore() ?? {};
  const query = createAdvancedQuery(submittedFilters) || DEFAULT_QUERY;
  const {
    httpClient
  } = getPremiumEssentialsSafe() ?? {};
  if (!httpClient) {
    return;
  }
  const queryClient = getSharedQueryClient();
  const baseFeatures = resolveFeatures(DEFAULT_FEATURES);
  queryClient.prefetchInfiniteQuery({
    queryKey: getRecommendationsQueryKey({
      query,
      features: baseFeatures,
      tlds,
      hasValidVouchers,
      productType,
      experimentIds
    }),
    queryFn: () => {
      const features = [...baseFeatures, RecommendationFeature.USE_BUSINESS_INFO];
      return DomainsApiClient.queries({
        httpClient
      }).listRecommendations({
        query,
        tlds: tlds ?? [],
        features: query === DEFAULT_QUERY ? features : baseFeatures,
        paging: {
          limit: LIMIT
        },
        ...(productType ? {
          productType: {
            domainProductType: productType
          }
        } : {}),
        ...declaredExperimentsField(experimentIds)
      });
    }
  });
};
export const useRecommendations = function (_temp2) {
  var _data$pages, _data$pages2;
  let {
    limit,
    enableOnEmptyQuery = true,
    shouldFetchRecommendations = true,
    uniqueSldsOnly = false,
    productType
  } = _temp2 === void 0 ? {
    limit: LIMIT,
    enableOnEmptyQuery: true,
    shouldFetchRecommendations: true,
    uniqueSldsOnly: false
  } : _temp2;
  const {
    tlds,
    ...submittedFilters
  } = CoreDomainSearchStore.useSubmittedFilters() ?? {};
  const [selectedFeatures, setSelectedFeatures] = useState(DEFAULT_FEATURES);
  const query = createAdvancedQuery(submittedFilters);
  const {
    siteId
  } = getPremiumEssentialsSafe() ?? {};
  const isLazyPricesOn = isLazyPricesEnabled();
  // Inert while lazy prices are on — `resolveFeatures` drops PRICING from this call and the ids
  // ride on `listDisplayPrices` instead. With lazy prices off this is the only path that carries
  // them, and without it the server returns ungated pricing and the arm renders control.
  const experimentIds = useDeclaredExperiments();
  const {
    data: domainsAccount
  } = DomainsApiClient.hooks.useDomainsAccount({}, true, {
    refetchOnMount: true,
    staleTime: 1000 * 60 * 3
  });
  const {
    listRecommendations
  } = DomainsApiClient.useQueries();
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: queryError,
    refetch
  } = useInfiniteQuery({
    queryKey: getRecommendationsQueryKey({
      query,
      features: resolveFeatures(selectedFeatures),
      tlds,
      hasValidVouchers: domainsAccount == null ? void 0 : domainsAccount.hasValidVouchers,
      uniqueSldsOnly,
      productType,
      experimentIds
    }),
    enabled: shouldFetchRecommendations && (!!query || enableOnEmptyQuery && !!siteId),
    staleTime: Infinity,
    cacheTime: Infinity,
    queryFn: async _ref2 => {
      let {
        pageParam
      } = _ref2;
      const features = [...resolveFeatures(DEFAULT_FEATURES), RecommendationFeature.USE_BUSINESS_INFO];
      return listRecommendations({
        query: query || DEFAULT_QUERY,
        tlds: tlds ?? [],
        features: query ? resolveFeatures(selectedFeatures) : features,
        paging: {
          limit,
          cursor: pageParam
        },
        ...(uniqueSldsOnly ? {
          uniqueSldsOnly
        } : {}),
        ...(productType ? {
          productType: {
            domainProductType: productType
          }
        } : {}),
        ...declaredExperimentsField(experimentIds)
      });
    },
    getNextPageParam: lastPage => {
      var _lastPage$pagingMetad, _lastPage$pagingMetad2;
      return lastPage != null && (_lastPage$pagingMetad = lastPage.pagingMetadata) != null && _lastPage$pagingMetad.hasNext ? lastPage == null || (_lastPage$pagingMetad2 = lastPage.pagingMetadata) == null || (_lastPage$pagingMetad2 = _lastPage$pagingMetad2.cursors) == null ? void 0 : _lastPage$pagingMetad2.next : undefined;
    }
  });
  const recommendations = (data == null || (_data$pages = data.pages) == null ? void 0 : _data$pages.flatMap(page => page.recommendations ?? [])) ?? [];
  const {
    pricesByDomain,
    isLoading: isPricingLoading,
    pricesExcludeTax
  } = useDisplayPrices(isLazyPricesOn ? recommendations : [], productType);
  const recommendationsWithPrices = isLazyPricesOn ? recommendations.map(recommendation => recommendation.domain && pricesByDomain.has(recommendation.domain) ? {
    ...recommendation,
    price: pricesByDomain.get(recommendation.domain)
  } : recommendation) : recommendations;
  const error = queryError ? classifyRecommendationsError(queryError) : !isLoading && recommendations.length === 0 ? 'NoRecommendations' : undefined;

  // Only when the user ends up with zero results: a failed fetchNextPage
  // sets queryError too, but earlier pages are still on screen — that is a
  // view-more failure, not a result-page error.
  const biError = recommendations.length > 0 ? undefined : queryError ? 'suggestions_call_failed' : !isLoading && (data == null ? void 0 : data.pages) != null ? 'suggestions_no_results' : undefined;
  const response = {
    recommendations: recommendationsWithPrices,
    hasNextPage,
    selectedFeatures,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    setSelectedFeatures,
    error,
    biError,
    query: query === DEFAULT_QUERY ? undefined : query,
    onRetry: () => refetch(),
    taxesNotIncluded: isLazyPricesOn ? pricesExcludeTax : data == null || (_data$pages2 = data.pages) == null || (_data$pages2 = _data$pages2[0]) == null ? void 0 : _data$pages2.pricesExcludeTax,
    isPricingLoading
  };
  return response;
};
//# sourceMappingURL=useRecommendations.js.map